import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Tooltip,
  useMediaQuery,
  Badge,
  Divider,
  alpha
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  AccountCircle,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  NightlightRound as DarkModeIcon,
  LightMode as LightModeIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import InlineSearchBar from '../common/InlineSearchBar';
import useAnalytics from '../../hooks/useAnalytics';
import NotificationsMenu from '../notifications/NotificationsMenu';
import SyncButton from '../common/SyncButton';

// Define drawerWidth constant to match the one in DashboardLayout
const drawerWidth = 260;

// Add TypewriterText component
const TypewriterText: React.FC<{ text: string }> = ({ text }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDisplayText('');
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return (
    <Typography
      variant="h6"
      component={motion.div}
      fontWeight={700}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {displayText}
    </Typography>
  );
};

// Notification animation variants
const notificationVariants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 }
};

interface AppBarProps {
  open: boolean;
  handleDrawerOpen: () => void;
  extraComponent?: React.ReactNode;
  isAdmin?: boolean;
}

const AppBar: React.FC<AppBarProps> = ({ open, handleDrawerOpen, extraComponent, isAdmin = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { themeMode, toggleThemeMode } = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { trackEvent } = useAnalytics();

  const darkMode = themeMode === 'dark';

  // Get current page title from location path
  const getPageTitle = () => {
    const path = location.pathname.split('/')[1];
    if (!path) return "Dashboard";
    return path.charAt(0).toUpperCase() + path.slice(1);
  };

  // User initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return "?";

    if (currentUser.displayName) {
      // If display name exists, use first letter of each part
      return currentUser.displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }

    // If no display name but email exists, use first letter of email
    if (currentUser.email) {
      return currentUser.email
        .split('@')[0]
        .charAt(0)
        .toUpperCase();
    }

    return "U";
  };

  // Get user display name or email for menu
  const getUserDisplayName = () => {
    if (!currentUser) return 'Guest';
    return currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
  };

  // Get user email for menu
  const getUserEmail = () => {
    if (!currentUser) return '';
    return currentUser.email || '';
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    trackEvent('ui_interaction', { action: 'open_profile_menu' });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      trackEvent('auth', { action: 'logout' });
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleSettings = () => {
    trackEvent('navigation', { destination: 'settings' });
    navigate('/settings');
    handleMenuClose();
  };

  const handleProfile = () => {
    trackEvent('navigation', { destination: 'profile' });
    navigate('/profile');
    handleMenuClose();
  };

  const handleDarkModeToggle = () => {
    toggleThemeMode();
    trackEvent('ui_interaction', { action: 'toggle_dark_mode', new_mode: themeMode === 'light' ? 'dark' : 'light' });
  };

  const handleHelp = () => {
    trackEvent('ui_interaction', { action: 'help_requested' });
    // Open help dialog or navigate to help page
  };

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        width: { md: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
        ml: { md: open ? `${drawerWidth}px` : 0 },
        transition: muiTheme.transitions.create(['margin', 'width'], {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.leavingScreen,
        }),
        backgroundImage: 'none',
        backdropFilter: 'blur(10px)',
        backgroundColor: darkMode
          ? alpha(muiTheme.palette.background.default, 0.9)
          : alpha(muiTheme.palette.background.paper, 0.9),
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.08)}`,
        zIndex: muiTheme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ position: 'relative' }}>
        {/* Left side - Menu Icon and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              mr: 2,
              display: { md: open ? 'none' : 'flex' },
              color: 'text.primary'
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Show title on mobile when drawer is closed */}
          {(isMobile && !open) && (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <img
                src="/assets/akmobooks.com.svg"
                alt="AkMo Books Logo"
                style={{ height: '23px', width: 'auto' }}
              />
              AkMo Books
            </Typography>
          )}

          {/* Page title with typewriter effect */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TypewriterText text={getPageTitle()} />
          </Box>
        </Box>

        {/* Centered search component */}
        <Box sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '280px', sm: '350px', md: '400px' },
          maxWidth: '90vw'
        }}>
          <InlineSearchBar isAdmin={isAdmin} />
        </Box>

        {/* Right side - User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          {/* Extra component slot */}
          {extraComponent}

          {/* Sync button */}
          <SyncButton />

          {/* Help button */}
          <Tooltip title="Help & Support">
            <IconButton
              size="large"
              color="inherit"
              onClick={handleHelp}
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>

          {/* Dark Mode Toggle */}
          <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton
              onClick={handleDarkModeToggle}
              color="inherit"
              size="large"
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <NotificationsMenu />

          {/* User Menu */}
          <Box sx={{ ml: 1 }}>
            <Tooltip title="Account & Settings">
              <IconButton
                onClick={handleProfileMenuOpen}
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                color="inherit"
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 2,
                sx: {
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: 2,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }
              }}
            >
              {/* User info section */}
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {getUserDisplayName()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getUserEmail()}
                </Typography>
              </Box>
              <Divider />

              {/* Menu items */}
              {!isAdmin && (
                <>
                  <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
                    <AccountCircle fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">My Profile</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
                    <SettingsIcon fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Settings</Typography>
                  </MenuItem>
                  <Divider />
                </>
              )}
              <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                <Typography variant="body2">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar; 